import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Store } from '@ngrx/store';
import { THDSAngularAuthService } from '@thds/auth/angular';
import { DlButtonModule } from '@thds/components/button';
import { DlFormFieldModule } from '@thds/components/form-field';
import { DlInputModule } from '@thds/components/input';
import { Observable, filter, first, map, startWith, tap } from 'rxjs';
import {
  AggregierteAuswertungLayoutDto,
  AggregierteAuswertungSpalteDto,
} from '../../api/models';
import * as fromAuswertungenLayouts from '../../store/auswertung-layouts/auswertung-layouts.selectors';
import { DragDropListComponent } from '../monatsauswertungen-layouts-editor/drag-drop-list/drag-drop-list.component';

export type EditorOutput = {
  name: string;
  kuerzel: string;
  spalten: string[];
};

@Component({
  selector: 'thds-aggregierteauswertungen-layouts-editor',
  standalone: true,
  imports: [
    CommonModule,
    DlButtonModule,
    DlFormFieldModule,
    DlInputModule,
    DragDropModule,
    ReactiveFormsModule,
    DragDropListComponent,
  ],
  templateUrl: './aggregierteauswertungen-layouts-editor.component.html',
  styleUrls: ['./aggregierteauswertungen-layouts-editor.component.scss'],
})
export class AggregierteAuswertungenLayoutsEditorComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly authService = inject(THDSAngularAuthService);
  public readonly aggregierteAuswertungenSpalten$: Observable<
    AggregierteAuswertungSpalteDto[] | undefined
  > = this.store.select(
    fromAuswertungenLayouts.selectAggregierteAuswertungenSpalten,
  );

  public readonly loading$: Observable<boolean> = this.store.select(
    fromAuswertungenLayouts.selectAggregierteAuswertungenSpaltenLoading,
  );

  public readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(255)],
    }),
    kuerzel: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(50)],
    }),
    aktiveSpalten: new FormControl<AggregierteAuswertungSpalteDto[]>([], {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  public readonly spalten = signal<AggregierteAuswertungSpalteDto[]>([]);
  @Output()
  public readonly submitForm = new EventEmitter<EditorOutput>();

  private originalEditorOutput = signal<EditorOutput | undefined>(undefined);

  public formValue = toSignal(
    this.form.valueChanges.pipe(
      map((x) => ({
        name: x.name || '',
        kuerzel: x.kuerzel || '',
        spalten: x.aktiveSpalten?.map((s) => s.id) || [],
      })),
      startWith({
        name: this.form.getRawValue().name,
        kuerzel: this.form.getRawValue().kuerzel,
        spalten: this.form.getRawValue().aktiveSpalten.map((s) => s.id),
      }),
    ),
    { initialValue: undefined },
  );

  public readonly hasChanges = computed(() => {
    const current = this.formValue();
    const original = this.originalEditorOutput();

    if (!current || !original) {
      return false;
    }

    const nameChanged = current.name !== original.name;
    const kuerzelChanged = current.kuerzel !== original.kuerzel;

    const spaltenChanged =
      current.spalten.length !== original.spalten.length ||
      current.spalten.some((id, index) => id !== original.spalten[index]);

    return nameChanged || kuerzelChanged || spaltenChanged;
  });

  @Input()
  public set aggregierteAuswertungLayout(
    layout: AggregierteAuswertungLayoutDto | undefined,
  ) {
    if (layout) {
      const values = {
        name: layout.name,
        kuerzel: layout.kuerzel,
        aktiveSpalten: layout.spalten.map((x) => ({
          name: x.name,
          id: x.spalteId,
          kuerzel: x.kuerzel,
        })),
      };
      this.form.patchValue(values);
      this.originalEditorOutput.set({
        name: layout.name,
        kuerzel: layout.kuerzel,
        spalten: layout.spalten.map((x) => x.spalteId),
      });
    } else {
      this.form.reset();
      this.originalEditorOutput.set(undefined);
    }
  }

  ngOnInit(): void {
    this.aggregierteAuswertungenSpalten$
      .pipe(
        filter(Boolean),
        first(),
        tap((spalten) => {
          this.spalten.set(spalten);
        }),
      )
      .subscribe();
  }

  onSubmit() {
    if (!this.hasScope('aggregierteAuswertungLayout:write')) {
      return;
    }

    if (this.form.valid) {
      const { name, kuerzel, aktiveSpalten } = this.form.getRawValue();
      const spalten = aktiveSpalten.map((spalte) => spalte.id);
      this.submitForm.emit({ name, kuerzel, spalten });
    }
  }

  hasScope(scope: string) {
    return this.authService.userScopes.includes(scope);
  }
}
